/**
 * Mapper/normalizer utilities for LXO Class Situation BE responses.
 *
 * BE field names may differ from the FE view model. These functions
 * translate API responses into the shapes expected by the component.
 */

/**
 * Maps a single BE class-situation list item to the FE table row shape.
 *
 * BE field -> FE field mapping:
 *  - teaching_hour_per_week / teachingHourPerWeek -> hourPerWeek
 *  - hour_week_name / hourWeekName      -> hourWeekName
 *  - teacher_hour_lesson / lessonStudied -> lessonStudied (fallback to lesson_studied)
 *  - avg_apollo_vintage / avgApolloVintage -> apolloVintage
 *  - epp_t8 / eppT8                    -> eppT8
 *  - epp_t9 / eppT9                    -> eppT9
 *  - estimate_acs_next_2month / estimateAcsNext2Month -> estimateAcsNext2Month
 *  - estimate_acs_next_3month / estimateAcsNext3Month -> estimateAcsNext3Month
 *  - recommend_to_close               -> recommendToClose
 *  - cso_in_charge / csoInCharge       -> csoInCharge
 *  - class_name / className            -> className
 *  - class_code / classCode            -> classCode
 *  - class_level / classLevel          -> classLevel
 *  - book_needed / bookNeeded          -> bookNeeded
 *  - row_color / rowColor              -> rowColor
 */
export function mapClassSituationItem(item) {
  if (!item) return null

  return {
    // identity / basic fields
    no: item.no ?? item.id ?? null,
    id: item.id ?? item.no ?? null,
    center: item.center ?? item.center_name ?? "",
    className: item.className ?? item.class_name ?? "",
    classLevel: item.classLevel ?? item.class_level ?? "",
    koc: item.koc ?? "",
    classCode: item.classCode ?? item.class_code ?? "",
    // Raw Hub class id used to build the Hub class-detail link.
    classId: item.classId ?? item.class_id ?? null,
    csoInCharge: item.csoInCharge ?? item.cso_in_charge ?? item.ec_in_charge ?? "",
    schedule: item.schedule ?? "",
    teacher: item.teacher ?? item.teachers ?? [],
    status: item.hubStatus ?? item.hub_status ?? item.status ?? "",
    // BE `status` is the capacity flag: "Full" / "Possible to fill".
    fillStatus: item.status ?? null,
    inProgressClassPerCenter:
      item.inProgressClassPerCenter ?? item.in_progress_class_per_center ?? null,

    // time / lesson fields
    hourPerWeek:
      item.hourPerWeek ?? item.teachingHourPerWeek ?? item.teaching_hour_per_week ?? null,
    hourWeekName: item.hourWeekName ?? item.hour_week_name ?? "",
    lessonStudied:
      item.lessonStudied ??
      item.teacherHourLesson ??
      item.teacher_hour_lesson ??
      item.lesson_studied ??
      "",
    hoursPerPeriod: item.hoursPerPeriod ?? item.hours_per_period ?? item.teachingHourPerLesson ?? item.teaching_hour_per_lesson ?? null,

    // modules
    modules: item.modules ?? { a: null, b: null, c: null },
    finalFillDate:
      item.finalFillDate ?? item.final_fill_date ?? item.tests?.finalToFill ?? null,

    // tests
    tests: {
      eoccDate: item.tests?.eoccDate ?? item.tests?.eocc_date ?? null,
      finalToFill: item.tests?.finalToFill ?? item.tests?.final_to_fill ?? null,
      moduleTestDate: item.tests?.moduleTestDate ?? item.tests?.module_test_date ?? item.moduleTestDate ?? item.module_test_date ?? null,
      unitTestDate: item.tests?.unitTestDate ?? item.tests?.unit_test_date ?? item.unitTestDate ?? item.unit_test_date ?? null,
    },

    // ACS by week (BE: acsLastWeek / acsThisWeek / acsNextWeek — Long)
    acsLastWeek: item.acsLastWeek ?? item.acs_last_week ?? item.lastWeek?.acs ?? item.last_week?.acs ?? null,
    acsThisWeek: item.acsThisWeek ?? item.acs_this_week ?? item.thisWeek?.acs ?? item.this_week?.acs ?? null,
    acsNextWeek: item.acsNextWeek ?? item.acs_next_week ?? item.nextWeek?.acs ?? item.next_week?.acs ?? null,

    // weekly data (inject per-week ACS so the detail table week block can render it)
    lastWeek: {
      ...(item.lastWeek ?? item.last_week ?? {}),
      acs: item.acsLastWeek ?? item.acs_last_week ?? item.lastWeek?.acs ?? item.last_week?.acs ?? null,
    },
    thisWeek: {
      ...(item.thisWeek ?? item.this_week ?? {}),
      acs: item.acsThisWeek ?? item.acs_this_week ?? item.thisWeek?.acs ?? item.this_week?.acs ?? null,
    },
    nextWeek: {
      ...(item.nextWeek ?? item.next_week ?? {}),
      acs: item.acsNextWeek ?? item.acs_next_week ?? item.nextWeek?.acs ?? item.next_week?.acs ?? null,
    },
    nextMonth: {
      ...(item.nextMonth ?? item.next_month ?? {}),
      epp:
        item.nextMonth?.epp ??
        item.next_month?.epp ??
        null,
      apolloVintage:
        item.apolloVintage ??
        item.avgApolloVintage ??
        item.avg_apollo_vintage ??
        item.nextMonth?.apolloVintage ??
        null,
    },

    // EPP & ACS estimates
    eppThisMonth: item.eppThisMonthCount ?? item.epp_this_month_count ?? item.thisMonthEppCount ?? item.this_month_epp_count ?? null,
    eppNextMonth: item.next1MonthEppCount ?? item.nextMonth?.next1MonthEppCount ?? item.next_month?.next_1_month_epp_count ?? null,
    estimateAcsThisMonth: item.acsThisMonth ?? item.acs_this_month ?? item.estimateAcsThisMonth ?? item.estimate_acs_this_month ?? null,
    estimateAcsNextMonth: item.estimateAcsNext1Month ?? item.estimate_acs_next_1month ?? null,
    eppT8: item.next2MonthEppCount ?? item.next_2_month_epp_count ?? item.nextMonth?.next2MonthEppCount ?? item.next_month?.next_2_month_epp_count ?? item.eppT8 ?? item.epp_t8 ?? null,
    eppT9: item.next3MonthEppCount ?? item.next_3_month_epp_count ?? item.nextMonth?.next3MonthEppCount ?? item.next_month?.next_3_month_epp_count ?? item.eppT9 ?? item.epp_t9 ?? null,
    estimateAcsNext2Month:
      item.estimateAcsNext2Month ?? item.estimate_acs_next_2month ?? null,
    estimateAcsNext3Month:
      item.estimateAcsNext3Month ?? item.estimate_acs_next_3month ?? null,
    recommendToClose: item.recommendToClose ?? item.recommend_to_close ?? "No",

    // misc
    // null is meaningful: no module finishes next month, so don't fall back to the next-week value.
    bookNeeded: item.bookNeeded !== undefined ? item.bookNeeded : item.book_needed ?? item.nextWeek?.bookNeeded ?? item.next_week?.book_needed ?? null,
    apolloVintage: item.apolloVintage ?? item.avgApolloVintage ?? item.avg_apollo_vintage ?? null,
    notes: item.notes ?? null,
    rowColor: item.rowColor ?? item.row_color ?? null,

  }
}

/**
 * Maps a list of BE items to FE row shape.
 */
export function mapClassSituationList(items) {
  if (!Array.isArray(items)) return []
  return items.map(mapClassSituationItem).filter(Boolean)
}

const readSummaryValue = (source, keys) => {
  for (const key of keys) {
    if (source[key] !== undefined) return source[key]
  }
  return undefined
}

export const CLASS_SITUATION_SUMMARY_GROUPS = [
  {
    title: "Overall",
    fields: [
      { key: "totalClasses", label: "Total classes", kind: "total", keys: ["totalClasses", "total_classes"] },
      {
        key: "avgApolloVintage",
        label: "Avg Apollo vintage",
        kind: "avg",
        keys: ["avgApolloVintage", "avg_apollo_vintage"],
      },
    ],
  },
  {
    title: "ACS",
    fields: [
      { key: "avgAcs", label: "Avg ACS", kind: "avg", keys: ["avgAcs", "avg_acs"] },
      {
        key: "avgEstimateAcsThisMonth",
        label: "Avg est. ACS this month",
        kind: "avg",
        keys: ["avgAcsThisMonth", "avg_acs_this_month", "avgEstimateAcsThisMonth", "avg_estimate_acs_this_month"],
      },
      {
        key: "avgEstimateAcsNextMonth",
        label: "Avg est. ACS next month",
        kind: "avg",
        keys: ["avgEstimateAcsNext1Month", "avg_estimate_acs_next_1month", "avg_estimate_acs_next_1_month"],
      },
      {
        key: "avgEstimateAcsNext2Month",
        label: "Avg est. ACS next 2 month",
        kind: "avg",
        keys: [
          "avgEstimateAcsNext2Month",
          "avg_estimate_acs_next_2month",
          "avg_estimate_acs_next_2_month",
          "avg_estimate_acs_next2_month",
        ],
      },
      {
        key: "avgAcsLastWeek",
        label: "Avg ACS last week",
        kind: "avg",
        keys: ["avgAcsLastWeek", "avg_acs_last_week"],
      },
      {
        key: "avgAcsThisWeek",
        label: "Avg ACS this week",
        kind: "avg",
        keys: ["avgAcsThisWeek", "avg_acs_this_week"],
      },
      {
        key: "avgAcsNextWeek",
        label: "Avg ACS next week",
        kind: "avg",
        keys: ["avgAcsNextWeek", "avg_acs_next_week"],
      },
    ],
  },
  {
    title: "Last week",
    fields: [
      {
        key: "avgLwAttendance",
        label: "Avg attendance",
        kind: "avg",
        keys: ["avgLwAttendance", "avg_lw_attendance"],
      },
      {
        key: "avgLwHomeLearningScore",
        label: "Avg home learning score",
        kind: "avg",
        keys: ["avgLwHomeLearningScore", "avg_lw_home_learning_score"],
      },
      {
        key: "avgLwHomeLearningCompletion",
        label: "Avg home learning completion",
        kind: "avg",
        keys: ["avgLwHomeLearningCompletion", "avg_lw_home_learning_completion"],
      },
      { key: "totalLwStudents", label: "Students", kind: "total", keys: ["totalLwStudents", "total_lw_students"] },
      { key: "totalLwOfftrack", label: "Offtrack", kind: "total", keys: ["totalLwOfftrack", "total_lw_offtrack"] },
    ],
  },
  {
    title: "This week",
    fields: [
      { key: "totalTwStudents", label: "Students", kind: "total", keys: ["totalTwStudents", "total_tw_students"] },
      { key: "totalTwNewStudent", label: "New student", kind: "total", keys: ["totalTwNewStudent", "total_tw_new_student"] },
      { key: "totalTwEpp", label: "EPP", kind: "total", keys: ["totalTwEpp", "total_tw_epp"] },
      { key: "totalTwFeedback", label: "Outstanding", kind: "total", keys: ["totalTwFeedback", "total_tw_feedback"] },
      { key: "totalTwOfftrack", label: "Offtrack", kind: "total", keys: ["totalTwOfftrack", "total_tw_offtrack"] },
    ],
  },
  {
    title: "Next week",
    fields: [
      { key: "totalNwStudents", label: "Students", kind: "total", keys: ["totalNwStudents", "total_nw_students"] },
      { key: "totalNwNewStudent", label: "New student", kind: "total", keys: ["totalNwNewStudent", "total_nw_new_student"] },
      { key: "totalNwEpp", label: "EPP", kind: "total", keys: ["totalNwEpp", "total_nw_epp"] },
      { key: "totalNwBookNeeded", label: "Book needed", kind: "total", keys: ["totalNwBookNeeded", "total_nw_book_needed"] },
    ],
  },
  {
    title: "Next month",
    fields: [
      { key: "totalNmNewStudent", label: "New student", kind: "total", keys: ["totalNmNewStudent", "total_nm_new_student"] },
      {
        key: "totalThisMonthEpp",
        label: "EPP this month",
        kind: "total",
        keys: ["totalEppThisMonth", "total_epp_this_month", "totalThisMonthEpp", "total_this_month_epp"],
      },
      {
        key: "totalNextMonthEpp",
        label: "EPP next month",
        kind: "total",
        keys: ["totalNext1MonthEpp", "total_next_1_month_epp", "total_next1_month_epp"],
      },
      {
        key: "totalNext2MonthEpp",
        label: "EPP next 2 month",
        kind: "total",
        keys: ["totalNext2MonthEpp", "total_next_2_month_epp", "total_next2_month_epp"],
      },
      { key: "totalNmBookNeeded", label: "Book needed", kind: "total", keys: ["totalNmBookNeeded", "total_nm_book_needed"] },
    ],
  },
]

export function formatSummaryMetric(value, kind) {
  if (kind === "avg") {
    if (value === null || value === undefined || value === "") return "-"
    const n = Number(value)
    return Number.isFinite(n) ? String(n) : "-"
  }
  if (value === null || value === undefined || value === "") return "0"
  const n = Number(value)
  return Number.isFinite(n) ? String(n) : "0"
}

/**
 * Maps data.summary (sibling of data.data) into the table footer.
 * AVG stays null when every class is null; TOTAL falls back to 0.
 */
export function mapClassSituationSummary(raw) {
  if (!raw || typeof raw !== "object") return null

  const summary = {
    summaryLabel: readSummaryValue(raw, ["summaryLabel", "summary_label"]) || "TOTAL / AVERAGE",
  }

  CLASS_SITUATION_SUMMARY_GROUPS.forEach((group) => {
    group.fields.forEach((field) => {
      const value = readSummaryValue(raw, field.keys)
      if (field.kind === "avg") {
        if (value === null || value === undefined || value === "") {
          summary[field.key] = null
          return
        }
        const n = Number(value)
        summary[field.key] = Number.isFinite(n) ? n : null
        return
      }
      const n = Number(value)
      summary[field.key] = Number.isFinite(n) ? n : 0
    })
  })

  return summary
}

export function buildClassSituationSummaryItems(summary) {
  if (!summary) return []
  return CLASS_SITUATION_SUMMARY_GROUPS.flatMap((group) =>
    group.fields.map((field) => ({
      label: `${group.title} — ${field.label}`,
      value: formatSummaryMetric(summary[field.key], field.kind),
    }))
  )
}

/**
 * Normalizes the BE pagination metadata into the FE shape.
 *
 * BE may return various shapes:
 *  - { totalPages, number, totalElements }  (Spring-style)
 *  - { total_page, current_page, total_elements }
 *  - { totalPage, currentPage, totalElements }
 */
export function normalizePagination(meta, fallbackPage = 0) {
  if (!meta) return { totalPage: 0, currentPage: fallbackPage, totalElements: 0 }

  return {
    totalPage: meta.totalPage ?? meta.totalPages ?? meta.total_page ?? 0,
    currentPage: meta.currentPage ?? meta.number ?? meta.current_page ?? fallbackPage,
    totalElements: meta.totalElements ?? meta.totalElement ?? meta.total_elements ?? 0,
  }
}

/**
 * Maps the new /number-block response into the FE summary state shape.
 */
export function mapNumberBlockResponse(data) {
  if (!data) return null
  const o = data.overall ?? {}
  const a = data.acs ?? {}
  const s = data.studentSituation ?? {}
  const c = data.classEvent ?? {}

  // Helper: read a field from the acs block first, then fall back to the
  // top-level response (BE may return these either nested or flat).
  const acsField = (...keys) => {
    for (const key of keys) {
      if (a[key] !== undefined && a[key] !== null) return a[key]
      if (data[key] !== undefined && data[key] !== null) return data[key]
    }
    return null
  }

  // BE now always returns null for stsNo*, attendanceLastWeekPct,
  // nbAbsent3plusConsecutive, nbWaitingToClass and nbNotRenewed6w — keep
  // null so the card shows "-" instead of a misleading 0.
  return {
    stsNo: o.stsNoThisWeek ?? null,
    totalStsVsLastWeek: o.stsNoLastWeek ?? null,
    totalStudentsNextWeek: o.stsNoNextWeek ?? null,
    noClass: o.classCount ?? 0,
    totalTeachingHr: o.totalTeachingHoursThisMonth ?? 0,
    attendanceLastWeek: o.attendanceLastWeekPct ?? null,
    acs: a.nbNormalAcs ?? 0,
    lowAcs: a.nbLowAcs ?? 0,
    criticalLowAcs: a.nbCriticalLowAcs ?? 0,
    overAcs: a.nbOverAcs ?? 0,

    // New ACS number-block fields (see task: 7 added BE fields).
    // acs (BigDecimal), acsLastWeek/acsThisWeek/acsNextWeek (Long),
    // acsFinalWeekOfMonth (Long), isLowAcs / isVeryLowAcs (Integer flag)
    acsValue: acsField("acs", "acsValue", "acs_value"),
    acsLastWeek: acsField("acsLastWeek", "acs_last_week"),
    acsThisWeek: acsField("acsThisWeek", "acs_this_week"),
    acsNextWeek: acsField("acsNextWeek", "acs_next_week"),
    acsFinalWeekOfMonth: acsField("acsFinalWeekOfMonth", "acs_final_week_of_month"),
    isLowAcs: acsField("isLowAcs", "is_low_acs"),
    isVeryLowAcs: acsField("isVeryLowAcs", "is_very_low_acs"),
    studentOutstanding: s.nbOutstanding ?? 0,
    absentOver2Consecutive: s.nbAbsent3plusConsecutive ?? null,
    eppThisMonth: s.nbEppThisMonth ?? 0,
    eppNextMonth: s.nbEppNextMonth ?? 0,
    eppNext2months: s.nbEppNext2months ?? 0,
    waitingToEnroll: s.nbWaitingToEnroll ?? 0,
    waitingToClass: s.nbWaitingToClass ?? null,
    stsDelay: s.nbDelay ?? 0,
    stsJoin1stLesson: c.stsJoin1stLesson ?? 0,
    firstLesson: c.nb1stLesson ?? 0,
    unitTest: c.nbUnitTest ?? 0,
    eocc: c.nbEocc ?? 0,
    parentsNotContacted6Weeks: c.nbNotRenewed6w ?? null,
  }
}

/**
 * Maps the BE count/summary response into the FE summary state shape.
 *
 * BE field -> FE field mapping:
 *  - sts_no / stsNo                          -> stsNo
 *  - no_class / noClass                      -> noClass
 *  - acs                                     -> acs
 *  - low_acs / lowAcs                        -> lowAcs
 *  - critical_low_acs / criticalLowAcs       -> criticalLowAcs
 *  - over_acs / overAcs                      -> overAcs
 *  - student_outstanding / studentOutstanding -> studentOutstanding
 *  - absent_over_2_consecutive / absentOver2Consecutive -> absentOver2Consecutive
 *  - total_teaching_hr / totalTeachingHr     -> totalTeachingHr
 *  - attendance_last_week / attendanceLastWeek -> attendanceLastWeek
 *  - total_sts_vs_last_week / totalStsVsLastWeek -> totalStsVsLastWeek
 *  - total_students_next_week / totalStudentsNextWeek -> totalStudentsNextWeek
 *  - epp_this_week_month_next_2_months / eppThisWeekMonthNext2Months -> eppThisWeekMonthNext2Months
 *  - waiting_to_enroll / waitingToEnroll     -> waitingToEnroll
 *  - waiting_to_class / waitingToClass       -> waitingToClass
 *  - sts_delay / stsDelay                   -> stsDelay
 *  - sts_join_1st_lesson / stsJoin1stLesson -> stsJoin1stLesson
 *  - first_lesson / firstLesson             -> firstLesson
 *  - unit_test / unitTest                   -> unitTest
 *  - eocc                                   -> eocc
 *  - parents_not_contacted_6_weeks / parentsNotContacted6Weeks -> parentsNotContacted6Weeks
 */
export function mapCountResponse(data) {
  if (!data) return null

  return {
    stsNo: data.stsNo ?? data.sts_no ?? 0,
    noClass: data.noClass ?? data.no_class ?? 0,
    acs: data.acs ?? 0,
    lowAcs: data.lowAcs ?? data.low_acs ?? 0,
    criticalLowAcs: data.criticalLowAcs ?? data.critical_low_acs ?? 0,
    overAcs: data.overAcs ?? data.over_acs ?? 0,
    studentOutstanding: data.studentOutstanding ?? data.student_outstanding ?? 0,
    absentOver2Consecutive:
      data.absentOver2Consecutive ?? data.absent_over_2_consecutive ?? 0,
    totalTeachingHr: data.totalTeachingHr ?? data.total_teaching_hr ?? 0,
    attendanceLastWeek: data.attendanceLastWeek ?? data.attendance_last_week ?? 0,
    totalStsVsLastWeek: data.totalStsVsLastWeek ?? data.total_sts_vs_last_week ?? 0,
    totalStudentsNextWeek:
      data.totalStudentsNextWeek ?? data.total_students_next_week ?? 0,
    eppThisWeekMonthNext2Months:
      data.eppThisWeekMonthNext2Months ??
      data.epp_this_week_month_next_2_months ??
      0,
    waitingToEnroll: data.waitingToEnroll ?? data.waiting_to_enroll ?? 0,
    waitingToClass: data.waitingToClass ?? data.waiting_to_class ?? 0,
    stsDelay: data.stsDelay ?? data.sts_delay ?? 0,
    stsJoin1stLesson: data.stsJoin1stLesson ?? data.sts_join_1st_lesson ?? 0,
    firstLesson: data.firstLesson ?? data.first_lesson ?? 0,
    unitTest: data.unitTest ?? data.unit_test ?? 0,
    eocc: data.eocc ?? 0,
    parentsNotContacted6Weeks:
      data.parentsNotContacted6Weeks ?? data.parents_not_contacted_6_weeks ?? 0,
  }
}
